import UserCustomSignatureParams from "@/domain/entities/user/UserCustomSignature";
import { queryKeys } from "@/lib/queryKeys";
import {
    userSignatureToDomainMapper,
    userSignatureToMailvistaMapper,
} from "@/mappers/user/userSignatureMapper";
import { useSdk } from "@cs_teamwaydev/mailvista-sdk_react";
import {
    useMutation,
    UseMutationResult,
    useQueryClient,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import useErrorToast from "../utils/useErrorToast";
import { useToast } from "../utils/useToast";

const useUpdateUserSignature = (): {
    updateUserSignature: UseMutationResult<
        void,
        Error,
        UserCustomSignatureParams
    >;
} => {
    const { userManager } = useSdk();
    const queryClient = useQueryClient();
    const showErrorToast = useErrorToast();
    const { toast } = useToast();
    const { t } = useTranslation();

    const updateUserSignatureFn = async ({
        id,
        userId,
        isFavorite,
        avatarURL,
        name,
        email,
        phone,
        jobTitle,
        companyName,
        website,
        other,
    }: UserCustomSignatureParams): Promise<void> => {
        const signatureParams = new UserCustomSignatureParams({
            id,
            userId,
            isFavorite,
            avatarURL,
            name,
            jobTitle,
            email,
            companyName,
            phone,
            website,
            other,
        });

        const signatureMailvista =
            userSignatureToMailvistaMapper(signatureParams);

        try {
            const result =
                await userManager.updateUserSignature(signatureMailvista);

            const resultSignatureParams = userSignatureToDomainMapper(result);

            queryClient.setQueryData(
                queryKeys.user.signature(),
                resultSignatureParams,
            );

            toast({
                description: t("settings.signature.form.saveSuccess"),
                variant: "success",
            });
        } catch (error) {
            console.error(error);
            showErrorToast({
                titleTranslationKey: "errors.signature.update",
            });
        }
    };

    const updateUserSignature = useMutation({
        mutationFn: (signatureParams: UserCustomSignatureParams) =>
            updateUserSignatureFn(signatureParams),
    });

    return {
        updateUserSignature,
    };
};

export default useUpdateUserSignature;
